import ContentBox from "../ContentBox/ContentBox";
import { Box, Grid, IconButton, MenuItem, SelectChangeEvent, Stack, Tooltip, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { getCaseLink, getProductData } from "../../services/Product/ProductService/productService";
import React, { SyntheticEvent, useEffect, useState } from "react";
import SearchBar from "../Algolia/SearchBar";
import { ALGOLIA_INDEX } from "../../config/_entrypoint";
import CaseHit from "../Case/CaseEntityPicker/Hit/CaseHit";
import { Hit } from "@algolia/client-search";
import { Delete, InfoOutlined } from "@mui/icons-material";
import useForm from "../../hooks/useForm";
import ValidatorTextField from "../Validator/ValidatorTextField";
import { convertToFloat, formValue, toIban } from "../../services/formServiceFunctions";
import { ibanFieldDefault, requiredFieldDefault } from "../../services/validationRules";
import SubmitButton from "../Button/SubmitButton";
import { apiGet, apiPost } from "../../services/Api/apiCall";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import ValidatorNumberField from "../Validator/ValidatorNumberField";
import { PayerAccount, WireTransfer } from "../../types/WireTransfer";
import InfoBox from "../InfoBox/InfoBox";
import { getWiretransferDisplayStatus, WIRETRANSFER_PAYER_ACCOUNTS } from "../../services/wiretransferService";
import { IbanInput } from "../MaskedInputs/IbanInput";
import InputAdornment from "@mui/material/InputAdornment";
import { getLabelFromHit } from "../../services/Board/algolia";
import ValidatorSelect from "../Validator/ValidatorSelect";
import LabeledSelect from "../Select/LabeledSelect";
import LegalbirdDropzone from "../Case/CaseUploadFile/LegalbirdDropzone";
import ApiClient from "../../services/ApiClient";
import { useSnackbar } from "notistack";
import { MediaObject } from "../../types/MediaObject";
import { getMediaObjectFilters } from "../../services/mediaObjectService";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "../../services/ReactQuery/reactQueryService";
import _ from "lodash";
import { HydraCollectionResult } from "../../types/HydraCollectionResult";
import { userHasOneOfTheseRoles } from "../../services/backofficeUserService";
import { Roles } from "../../types/BackofficeUser";
import { translate } from "../../services/Translations/translatorService";
import { scrollToAnchor } from "../../theme/commonStyles";
import { ProductUnion } from "../../types/ProductUnion";
import dayjs from "dayjs";

type AddWiretransferProps = {
  currentCase?: ProductUnion;
  postSubmitCallback?: Function;
};

type WiretransferTemplateConfig = {
  allowedPayerAccounts: PayerAccount[];
};

const allPayerAccounts = WIRETRANSFER_PAYER_ACCOUNTS.map((account) => account.value);

const wiretransferTemplateConfig: Record<string, WiretransferTemplateConfig> = {
  // Sonstige Überweisung
  other: {
    allowedPayerAccounts: allPayerAccounts,
  },
  // Gebührenbegleichung (REA Gericht/Melderegister)
  feePayment: {
    allowedPayerAccounts: [PayerAccount.lbra],
  },
  // Fremdgeld
  thirdPartyFunds: {
    allowedPayerAccounts: [PayerAccount.lbra],
  },
  // Expenses
  expenses: {
    allowedPayerAccounts: [PayerAccount.legalbirdMain],
  },
  // Rückzahlung GKV an RSV
  creditNoteCourtAdvancePayment: {
    allowedPayerAccounts: [PayerAccount.lbra],
  },
  // Rückzahlung Überschuss aus Abschlussrechnung an RSV
  creditNoteSurplusFinalPaymentInsurer: {
    allowedPayerAccounts: [PayerAccount.lbra],
  },
  // Rückzahlung Überschuss aus Abschlussrechnung an Mandantschaft
  creditNoteSurplusFinalPaymentClient: {
    allowedPayerAccounts: [PayerAccount.lbra],
  },
  // Rückzahlung Gebühren an RSV
  creditNoteFeesInsurer: {
    allowedPayerAccounts: [PayerAccount.lbra],
  },
  // Rückzahlung Partnerpauschale an RSV
  creditNotePartnerFeeInsurer: {
    allowedPayerAccounts: [PayerAccount.legalbirdRevenue],
  },
  // Begleichung LBRA Rechnung (nicht fallbezogen)
  creditNoteLawyerInvoice: {
    allowedPayerAccounts: [PayerAccount.lbra],
  },
  // Begleichung TV Rechnung (abseits von AdvoAssist)
  nonAdvoAssistRepresentativeInvoice: {
    allowedPayerAccounts: [PayerAccount.lbra],
  },
};

export default function AddWiretransfer({ currentCase, postSubmitCallback }: AddWiretransferProps) {
  const [caseHit, setCaseHit] = useState<Hit<any> | null>(null);
  const [caseWiretransferCollection, setCaseWiretransferCollection] = useState<WireTransfer[] | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [updateKey, setUpdateKey] = useState(0);
  const [isFetchingCaseData, setisFetchingCaseData] = useState(false);
  const currentUser = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const [templateType, setTemplateType] = useState("");
  const isExpensesTemplate = templateType === "expenses";

  let mediaObjectsFilter: Partial<ProductUnion> = {
    id: 0,
    productClassName: "divorce",
  };
  const caseHitToProxyCase = (caseHit: Hit<any>) => {
    return {
      id: caseHit.productId,
      productClassName: caseHit.product,
      caseInsurance: {
        caseReferenceNumber: caseHit.caseReferenceNumber,
      },
      reference: caseHit.reference,
    };
  };

  const hasCorrespondingCase = !!(currentCase || caseHit);
  const correspondingCase = hasCorrespondingCase ? currentCase || caseHitToProxyCase(caseHit) : null;

  if (hasCorrespondingCase) {
    mediaObjectsFilter = getMediaObjectFilters(correspondingCase!, currentUser);
  }

  const getCaseId = () => {
    return correspondingCase?.id;
  };

  const { data: mediaObjectsCollection } = useQuery<HydraCollectionResult<MediaObject>>({
    queryKey: queryKeys.collection("media_objects", mediaObjectsFilter),
    queryFn: () => fetchCollection("media_objects", mediaObjectsFilter),
    enabled: hasCorrespondingCase
  });

  const initialValues = {
    recipientName: "",
    amount: "",
    subject: "",
    iban: "",
    payerAccount: PayerAccount.lbra,
    typeOfPayment: "",
    selectedMediaObjectId: "",
  };

  const onSubmit = async ({ values }: any) => {
    let data = { ...values };
    data.amount = convertToFloat(data.amount);
    data.createdBy = currentUser["@id"];
    data.caseId = getCaseId();
    data.iban = toIban(data.iban);

    const missingFileMessage = "Es muss ein Beleg angehangen werden";

    if (!data.caseId) {
      if (!files[0]) {
        enqueueSnackbar(missingFileMessage, {
          variant: "error",
          isNonInteractive: true,
          persist: true,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        });
        return;
      }

      let headers = new Headers();
      headers.set("Content-Type", "multipart/form-data");

      const file = files[0];
      let fileName = file.name;

      if (isExpensesTemplate) {
        fileName = "Expenses_" + currentUser.person.fullname.replace(/\s/g, "_") + "_vom_" + dayjs().format("YYYYMMDD") + ".pdf";
      }

      let formData = new FormData();
      formData.append("file", file, fileName);
      formData.append("fieldname", "wireTransferReceipt");
      let mediaObject: MediaObject;
      try {
        mediaObject = await ApiClient.post("media_objects", {
          headers: headers,
          body: formData,
        });
        data.receipt = mediaObject["@id"];
      } catch (e) {
        enqueueSnackbar("Die Datei konnte nicht hochgeladen werden", {
          variant: "error",
          isNonInteractive: true,
          persist: true,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        });
        return;
      }
    }
    if (data.caseId && mediaObjectsCollection) {
      const selectedMediaObject = mediaObjectsCollection["hydra:member"].find(
        (mediaObject: MediaObject) => mediaObject.id + "" === formValue(values, "selectedMediaObjectId")
      );

      data.receipt = !!selectedMediaObject && selectedMediaObject["@id"];
    }

    if (!data.receipt) {
      enqueueSnackbar("Technischer Fehler: Die Datei konnte nicht hinzugefügt werden", {
        variant: "error",
        isNonInteractive: true,
        persist: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
      return;
    }

    await apiPost("wiretransfers", data);
    setCaseHit(null);
    clearForm();
    setFiles([]);
    setUpdateKey((prevKey) => prevKey + 1);
    if (postSubmitCallback) {
      postSubmitCallback();
    }
    if (!currentCase) {
      // we must not fetch the wireTransfers again here since the value of caseHit is still the old one until the next rerender,
      // so it would fetch the wireTransfers for the previously selected case
      // currentCase is set on the case accounting page, but not on the wire transfer page under tools, where caseHit is used
      return;
    }
    fetchCaseWireTransfers();
  };

  const { values, errors, handleChange, handleSubmit, registerValidators, handleBlur, clearForm, isLoading } = useForm({
    initialValues,
    onSubmit,
  });

  useEffect(() => {
    setCaseWiretransferCollection(null);
    fetchCaseWireTransfers();
  }, [caseHit, currentCase]);

  const onHitClick = async (hit: Hit<any>, event: SyntheticEvent<HTMLElement> | undefined) => {
    event && event.preventDefault();
    setCaseHit(hit);
  };

  const fetchCaseWireTransfers = () => {
    if (!getCaseId()) {
      setCaseWiretransferCollection(null);
      return;
    }
    apiGet(`wiretransfers?caseId=${getCaseId()}&exists[deletedDate]=false`).then((result) =>
      setCaseWiretransferCollection(result["hydra:member"])
    );
  };

  const getSimilarExistingWiretransfer = () => {
    if (!caseWiretransferCollection || !values.amount) {
      return null;
    }

    return caseWiretransferCollection.find((wiretransfer) => wiretransfer.amount === parseFloat(values.amount)) || null;
  };

  const similarExistingWiretransfer = getSimilarExistingWiretransfer();

  const handlePrefill = ({
    payerAccount,
    typeOfPayment,
    subject,
  }: {
    payerAccount?: PayerAccount;
    typeOfPayment?: string;
    subject?: string;
  }) => {
    if (payerAccount) {
      handleChange({
        target: {
          name: "payerAccount",
          value: payerAccount,
        },
      });
    }
    if (typeOfPayment) {
      handleChange({
        target: {
          name: "typeOfPayment",
          value: typeOfPayment,
        },
      });
    }
    if (subject) {
      handleChange({
        target: {
          name: "subject",
          value: subject,
        },
      });
    }
  };

  const handleBankData = async () => {
    let caseData = currentCase;

    if (!caseData && caseHit) {
      setisFetchingCaseData(true);
      caseData = await ApiClient.get(`${getProductData(caseHit.product, "apiUri")}/${caseHit.productId}`);
      setisFetchingCaseData(false);
    }

    if (caseData && caseData.caseInsurance.insurer && caseData.caseInsurance.insurer.bankdata) {
      handleChange({
        target: {
          name: "recipientName",
          value: caseData.caseInsurance.insurer.bankdata.accountOwnerFullName,
        },
      });

      handleChange({
        target: {
          name: "iban",
          value: caseData.caseInsurance.insurer.bankdata.iban,
        },
      });
    }
  };

  const handleTemplateChange = (event: SelectChangeEvent) => {
    const templateType = event.target.value;

    setTemplateType(templateType);

    switch (templateType) {
      case "other":
        break;
      case "feePayment":
        handlePrefill({
          payerAccount: PayerAccount.lbra,
          typeOfPayment: "caseUnrelated",
          subject: hasCorrespondingCase ? "/" + correspondingCase!.reference : undefined,
        });
        break;
      case "thirdPartyFunds":
        handlePrefill({
          payerAccount: PayerAccount.lbra,
          typeOfPayment: "thirdPartyFunds",
          subject: hasCorrespondingCase ? "Weiterleitung Zahlung von - " + correspondingCase!.reference : undefined,
        });
        break;
      case "expenses":
        handlePrefill({
          payerAccount: PayerAccount.legalbirdMain,
          typeOfPayment: "caseUnrelated",
          subject: "Expenses " + currentUser.person.fullname + " vom " + dayjs().format("DD.MM.YYYY"),
        });
        break;
      case "creditNoteCourtAdvancePayment":
        handlePrefill({
          payerAccount: PayerAccount.lbra,
          typeOfPayment: "thirdPartyFunds",
          subject: hasCorrespondingCase
            ? "Leistungs-/Schadennummer " +
              correspondingCase!.caseInsurance.caseReferenceNumber +
              " - Rueckzahlung Gerichtskostenueberschuss"
            : undefined,
        });
        handleBankData();
        break;
      case "creditNoteSurplusFinalPaymentInsurer":
        handlePrefill({
          payerAccount: PayerAccount.lbra,
          typeOfPayment: "feeRefund",
          subject: hasCorrespondingCase
            ? "Leistungs-/Schadennummer " +
              correspondingCase!.caseInsurance.caseReferenceNumber +
              " - Rueckzahlung Ueberschuss aus Abschlussrechnung"
            : undefined,
        });
        handleBankData();
        break;
      case "creditNoteSurplusFinalPaymentClient":
        handlePrefill({
          payerAccount: PayerAccount.lbra,
          typeOfPayment: "feeRefund",
          subject: hasCorrespondingCase
            ? "Aktenzeichen " + correspondingCase!.reference + " - Rueckzahlung Ueberschuss aus Abschlussrechnung"
            : undefined,
        });
        break;
      case "creditNoteFeesInsurer":
        handlePrefill({
          payerAccount: PayerAccount.lbra,
          typeOfPayment: "feeRefund",
          subject: hasCorrespondingCase
            ? "Gebührenrückerstattung Leistungs-/Schadennummer " + correspondingCase!.caseInsurance.caseReferenceNumber
            : undefined,
        });
        handleBankData();
        break;
      case "creditNotePartnerFeeInsurer":
        handlePrefill({
          payerAccount: PayerAccount.legalbirdRevenue,
          typeOfPayment: "feeRefund",
          subject: hasCorrespondingCase
            ? "Gebührenrückerstattung Leistungs-/Schadennummer " + correspondingCase!.caseInsurance.caseReferenceNumber
            : undefined,
        });
        handleBankData();
        break;
      case "creditNoteLawyerInvoice":
        handlePrefill({
          payerAccount: PayerAccount.lbra,
          typeOfPayment: "caseUnrelated",
          subject: undefined,
        });
        break;
      case "nonAdvoAssistRepresentativeInvoice":
        handlePrefill({
          payerAccount: PayerAccount.lbra,
          typeOfPayment: "caseUnrelated",
          subject: hasCorrespondingCase ? "/" + correspondingCase!.reference : undefined,
        });
        break;
      default:
        break;
    }
  };

  const allowedPayerAccounts = wiretransferTemplateConfig[templateType]?.allowedPayerAccounts || allPayerAccounts;

  return (
    <ContentBox headline="Neue Überweisung erstellen">
      <Box sx={scrollToAnchor} id={"createNewWiretransfer"} />
      {!currentCase && (
        <>
          <Typography variant={"h4"} sx={{ textAlign: "center", marginBottom: "1rem" }}>
            Fallzuordnung (optional)
          </Typography>
          <Stack
            direction={"row"}
            spacing={2}
            sx={{
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box>
              {!!caseHit?.productId ? (
                <>
                  <Link
                    style={{ textDecoration: "none" }}
                    to={getCaseLink({ productClassName: caseHit.product, id: caseHit.productId })}
                    target={"_blank"}
                  >
                    {getLabelFromHit(caseHit) || "Zum Fall"}
                  </Link>
                  <IconButton onClick={() => setCaseHit(null)}>
                    <Delete />
                  </IconButton>
                </>
              ) : (
                "Unzugeordnet"
              )}
            </Box>
            <Box>
              <SearchBar
                searchConfig={{
                  indexName: ALGOLIA_INDEX,
                }}
                resultListConfig={{
                  itemSize: 330,
                  width: "100%",
                }}
                HitComponent={CaseHit}
                onHitClick={onHitClick}
              />
            </Box>
          </Stack>
          <Typography variant={"h4"} sx={{ textAlign: "center", margin: "1rem 0" }}>
            Überweisungsdaten
          </Typography>
        </>
      )}
      <Grid
        container
        spacing={2}
        sx={{
          justifyContent: "center",
        }}
      >
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <LabeledSelect
            label={"Überweisungsvorlage"}
            onChange={handleTemplateChange}
            disabled={isFetchingCaseData}
            value={templateType}
          >
            {userHasOneOfTheseRoles(currentUser, [Roles.admin, Roles.accounting]) && (
              <MenuItem value={"other"}>{translate("wireTransfer.wireTransferTemplates.other")}</MenuItem>
            )}
            <MenuItem value={"feePayment"} disabled={!correspondingCase}>
              {translate("wireTransfer.wireTransferTemplates.feePayment")}
            </MenuItem>
            <MenuItem value={"thirdPartyFunds"} disabled={!correspondingCase}>
              {translate("wireTransfer.wireTransferTemplates.thirdPartyFunds")}
            </MenuItem>
            <MenuItem value={"expenses"}>{translate("wireTransfer.wireTransferTemplates.expenses")}</MenuItem>
            <MenuItem value={"creditNoteCourtAdvancePayment"} disabled={!correspondingCase}>
              {translate("wireTransfer.wireTransferTemplates.creditNoteCourtAdvancePayment")}
            </MenuItem>
            <MenuItem value={"creditNoteSurplusFinalPaymentInsurer"} disabled={!correspondingCase}>
              {translate("wireTransfer.wireTransferTemplates.creditNoteSurplusFinalPaymentInsurer")}
            </MenuItem>
            <MenuItem value={"creditNoteSurplusFinalPaymentClient"} disabled={!correspondingCase}>
              {translate("wireTransfer.wireTransferTemplates.creditNoteSurplusFinalPaymentClient")}
            </MenuItem>
            <MenuItem value={"creditNoteFeesInsurer"} disabled={!correspondingCase}>
              {translate("wireTransfer.wireTransferTemplates.creditNoteFeesInsurer")}
            </MenuItem>
            <MenuItem value={"creditNotePartnerFeeInsurer"} disabled={!correspondingCase}>
              {translate("wireTransfer.wireTransferTemplates.creditNotePartnerFeeInsurer")}
            </MenuItem>
            <MenuItem value={"creditNoteLawyerInvoice"} disabled={correspondingCase !== null}>
              {translate("wireTransfer.wireTransferTemplates.creditNoteLawyerInvoice")}
            </MenuItem>
            <MenuItem value={"nonAdvoAssistRepresentativeInvoice"} disabled={!correspondingCase}>
              {translate("wireTransfer.wireTransferTemplates.nonAdvoAssistRepresentativeInvoice")}
            </MenuItem>
          </LabeledSelect>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 8,
          }}
        />
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <ValidatorTextField
            label={"Empfänger"}
            onChange={handleChange}
            onBlur={handleBlur}
            name={"recipientName"}
            value={formValue(values, "recipientName")}
            validators={[...requiredFieldDefault]}
            registerValidators={registerValidators}
            error={!!errors["recipientName"]}
            helperText={errors["recipientName"]}
            isHighlighted={false}
            initialDependentValidationFields={[]}
            disabled={isFetchingCaseData}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <ValidatorTextField
            label={"IBAN"}
            onChange={handleChange}
            onBlur={handleBlur}
            name={"iban"}
            value={formValue(values, "iban")}
            validators={[...requiredFieldDefault, ...ibanFieldDefault]}
            InputProps={{
              inputComponent: IbanInput,
              startAdornment: <InputAdornment position="start">DE</InputAdornment>,
            }}
            registerValidators={registerValidators}
            error={!!errors["iban"]}
            helperText={errors["iban"]}
            isHighlighted={false}
            initialDependentValidationFields={[]}
            disabled={isFetchingCaseData}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <ValidatorNumberField
            fieldType={"money"}
            numberType={"float"}
            label={"Betrag"}
            onChange={handleChange}
            onBlur={handleBlur}
            name={"amount"}
            value={formValue(values, "amount")}
            registerValidators={registerValidators}
            error={!!errors["amount"]}
            helperText={errors["amount"]}
            validators={requiredFieldDefault}
            isHighlighted={false}
            dependentValidationFields={[]}
            disabled={isFetchingCaseData}
          />
        </Grid>
        <Grid size={10}>
          <ValidatorTextField
            label={"Verwendungszweck"}
            onChange={handleChange}
            onBlur={handleBlur}
            name={"subject"}
            value={formValue(values, "subject")}
            validators={[...requiredFieldDefault]}
            registerValidators={registerValidators}
            error={!!errors["subject"]}
            helperText={errors["subject"] || "Maximal 115 Zeichen"}
            slotProps={{
              htmlInput: {
                maxLength: 115,
              },
            }}
            isHighlighted={false}
            initialDependentValidationFields={[]}
            disabled={isFetchingCaseData || isExpensesTemplate}
          />
        </Grid>
        <Grid size={2}>
          <Stack
            sx={{
              justifyContent: "center",
              alignItems: "center",
              height: "85%",
            }}
          >
            <Tooltip title="Muster in Confluence" arrow>
              <IconButton
                onClick={() =>
                  window.open(
                    "https://legalbird.atlassian.net/wiki/spaces/OP/pages/3388178435/Musterverwendungszwecke+berweisungen",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                <InfoOutlined />
              </IconButton>
            </Tooltip>
          </Stack>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <ValidatorSelect
            label={"Payer Account"}
            name={"payerAccount"}
            value={formValue(values, "payerAccount")}
            onChange={handleChange}
            onBlur={handleBlur}
            registerValidators={registerValidators}
            validators={requiredFieldDefault}
            error={!!errors["payerAccount"]}
            helperText={errors["payerAccount"]}
            disabled={isFetchingCaseData || allowedPayerAccounts.length === 1}
          >
            {WIRETRANSFER_PAYER_ACCOUNTS.filter((account) => allowedPayerAccounts.includes(account.value)).map(
              (account) => (
                <MenuItem key={account.value} value={account.value}>
                  {account.label}
                </MenuItem>
              )
            )}
          </ValidatorSelect>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <ValidatorSelect
            label={translate("wireTransfer.typeOfPayment.label")}
            name={"typeOfPayment"}
            value={formValue(values, "typeOfPayment")}
            onChange={handleChange}
            onBlur={handleBlur}
            registerValidators={registerValidators}
            validators={requiredFieldDefault}
            error={!!errors["typeOfPayment"]}
            helperText={errors["typeOfPayment"]}
            disabled={isFetchingCaseData || isExpensesTemplate}
          >
            <MenuItem value={"standard"}>{translate("wireTransfer.typeOfPayment.values.standard")}</MenuItem>
            <MenuItem value={"feeRefund"}>{translate("wireTransfer.typeOfPayment.values.feeRefund")}</MenuItem>
            <MenuItem value={"thirdPartyFunds"}>
              {translate("wireTransfer.typeOfPayment.values.thirdPartyFunds")}
            </MenuItem>
            <MenuItem value={"caseUnrelated"}>{translate("wireTransfer.typeOfPayment.values.caseUnrelated")}</MenuItem>
          </ValidatorSelect>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        />
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          {getCaseId() ? (
            <ValidatorSelect
              label={"Zugehöriges Dokument"}
              name={"selectedMediaObjectId"}
              value={formValue(values, "selectedMediaObjectId")}
              onChange={handleChange}
              registerValidators={registerValidators}
              validators={requiredFieldDefault}
              error={!!_.get(errors, "selectedMediaObjectId")}
              helperText={_.get(errors, "selectedMediaObjectId")}
              disabled={isFetchingCaseData}
            >
              {mediaObjectsCollection &&
                _.map(mediaObjectsCollection["hydra:member"], (mediaObject) => (
                  <MenuItem
                    key={mediaObject.id}
                    value={mediaObject.id + ""}
                    disabled={mediaObject.mimeType !== "application/pdf" || mediaObject.size > 20000000}
                  >
                    {mediaObject.description}
                  </MenuItem>
                ))}
            </ValidatorSelect>
          ) : (
            <LegalbirdDropzone files={files} setFiles={setFiles} acceptedFiles={["application/pdf"]} key={updateKey} />
          )}
        </Grid>
        {!!similarExistingWiretransfer && (
          <Grid size={12}>
            <InfoBox>
              Wichtiger Hinweis: Am {dayjs(similarExistingWiretransfer.created).format("DD.MM.YYYY")} wurde bereits eine
              Überweisung über diesen Betrag eingestellt (Status der Überweisung:{" "}
              {getWiretransferDisplayStatus(similarExistingWiretransfer)}).{" "}
              <strong>Bitte am Fall auf Doppelüberweisung prüfen.</strong> Soll diese Überweisung dennoch eingestellt
              werden?
            </InfoBox>
          </Grid>
        )}

        <Grid size={12}>
          <SubmitButton
            onClick={handleSubmit}
            variant={"contained"}
            isLoading={isLoading}
            disabled={(currentCase && caseWiretransferCollection === null) || isFetchingCaseData}
          >
            Speichern
          </SubmitButton>
        </Grid>
      </Grid>
    </ContentBox>
  );
}
